import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, Loader2, BadgeCheck, FileCheck2, Mail, Globe, Building2,
  MapPin, ArrowRight, Sparkles, ExternalLink,
} from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";
import { PublicFooter } from "@/components/PublicShell";
import { InviteToRfqDialog } from "@/components/InviteToRfqDialog";
import { VisitorRfqDialog } from "@/components/VisitorRfqDialog";
import { ReferralBanner } from "@/components/ReferralBanner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { toast } from "sonner";

const pub = axios.create({ baseURL: API });

export const VendorLogo = ({ vendor, size = 56 }) =>
  vendor.has_logo ? (
    <img
      src={`${API}/public/vendors/${vendor.id}/logo`}
      alt={vendor.company_name}
      style={{ width: size, height: size }}
      className="rounded-xl object-contain bg-white border border-border p-1.5 shrink-0"
    />
  ) : (
    <div
      style={{ width: size, height: size, fontSize: size * 0.34 }}
      className="rounded-xl bg-primary/8 border border-primary/15 text-primary font-display font-extrabold tracking-tight flex items-center justify-center shrink-0"
    >
      {(vendor.company_name || "?")
        .split(/\s+/)
        .filter((w) => /[A-Za-z]/.test(w[0] || ""))
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join("")}
    </div>
  );

export const Badges = ({ v }) => (
  <div className="flex flex-wrap items-center gap-1.5">
    {v.auto_quote && (
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span data-testid="badge-auto-quote" tabIndex={0} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-soft text-brand border border-brand/25 text-[11px] font-bold cursor-help">
              <BadgeCheck className="w-3 h-3" /> Auto quote on
            </span>
          </TooltipTrigger>
          <TooltipContent data-testid="badge-auto-quote-tip" side="top" className="max-w-[240px] text-center">
            This vendor prices your BOQ automatically from their catalogue — expect a fast, itemised quote.
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )}
    {v.documents_ok && (
      <span data-testid="badge-docs-ok" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent text-primary border border-primary/20 text-[11px] font-bold">
        <FileCheck2 className="w-3 h-3" /> Documents updated
      </span>
    )}
    {v.promoted && (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 text-[11px] font-bold">
        <Sparkles className="w-3 h-3" /> {["pro", "gold"].includes(v.plan) ? "Gold" : "Silver"} vendor
      </span>
    )}
  </div>
);

export const copyEmail = (email) => {
  if (!email) return toast.error("This vendor has not published an email yet");
  navigator.clipboard?.writeText(email);
  toast.success(`Email copied — ${email}`);
};

export default function Marketplace({ embedded = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isContractor = user?.account_type === "contractor";
  const [inviteVendor, setInviteVendor] = useState(null);
  const [visitorVendor, setVisitorVendor] = useState(null);
  const [trades, setTrades] = useState([]);
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [country, setCountry] = useState(user?.country || "");
  const [region, setRegion] = useState("");
  const [q, setQ] = useState("");
  const [trade, setTrade] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [online, setOnline] = useState(null);
  const [onlineLoading, setOnlineLoading] = useState(false);

  useEffect(() => {
    pub.get("/public/trades").then((r) => setTrades(r.data.trades)).catch(() => {});
    pub.get("/public/countries").then((r) => {
      setCountries(r.data.countries || []);
      setRegions(r.data.regions || []);
    }).catch(() => {});
  }, []);

  const search = async (query = q, tr = trade, ct = country, rg = region) => {
    setLoading(true);
    setOnline(null);
    try {
      const { data } = await pub.get("/public/vendors", { params: { q: query, trade: tr, country: ct, region: rg } });
      setData(data);
    } catch {
      toast.error("Search failed, please try again");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { search("", "", user?.country || ""); /* initial listing, default to my country */ }, []);

  const searchOnline = async () => {
    setOnlineLoading(true);
    try {
      const { data } = await pub.get("/public/online-search", { params: { q } });
      setOnline(data);
    } catch {
      toast.error("Could not search online right now");
    } finally {
      setOnlineLoading(false);
    }
  };

  const promoted = useMemo(() => (data?.vendors || []).filter((v) => v.promoted), [data]);

  return (
    <div className={embedded ? "" : "min-h-screen bg-background"}>
      {embedded && user && <ReferralBanner />}
      {!embedded && (
      <header className="border-b border-border bg-card/80 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" data-testid="marketplace-brand" className="flex items-center gap-3">
            <img src="/matiqs-logo.png" alt="Matiqs" className="brand-logo h-8 w-auto" />
            <span className="hidden sm:block text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-l border-border pl-3">
              Vendor Marketplace
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Link data-testid="nav-how-it-works" to="/how-it-works" className="hidden sm:block px-3 py-1.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">
              How it works
            </Link>
            <Link data-testid="nav-faq" to="/faq" className="hidden sm:block px-3 py-1.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">
              FAQ
            </Link>
            <Button data-testid="vendor-signin-button" variant="outline" onClick={() => navigate("/login?type=buyer")} className="h-10 font-semibold">
              Contractor sign in
            </Button>
            <Button data-testid="marketplace-signup-button" onClick={() => navigate("/login?type=buyer&mode=register")} className="hidden sm:flex h-10 font-semibold">
              Sign up free
            </Button>
          </div>
        </div>
      </header>
      )}

      <section className="relative border-b border-border overflow-hidden">
        <div className="absolute inset-0 grid-fade pointer-events-none" />
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[680px] h-[380px] rounded-full bg-primary/10 blur-3xl pointer-events-none" />
        <div className={`relative max-w-3xl mx-auto px-5 text-center ${embedded ? "py-8" : "py-16 md:py-20"}`}>
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {!embedded && (
              <>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-soft text-brand text-[11px] font-bold uppercase tracking-[0.16em] border border-brand/20">
              <Sparkles className="w-3 h-3" /> For contractors
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-slate-900 dark:text-white mt-6">
              Verified Network.<br />
              <span className="text-primary">Automated Quotations.</span><br />
              Faster Results.
            </h1>
            <p className="text-base md:text-lg text-muted-foreground mt-5 max-w-xl mx-auto">
              Search suppliers and subcontractors by trade. Vendors with automatic quoting turned on can price your BOQ in minutes.
            </p>
              </>
            )}
            {embedded && (
              <h1 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
                Find your next vendor
              </h1>
            )}
            <div className="mt-9 w-full max-w-2xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-2 items-stretch p-2 rounded-2xl bg-card border border-border shadow-xl shadow-slate-900/5">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    data-testid="marketplace-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && search()}
                    placeholder="Try 'metalwork', 'electrical', 'HVAC subcontractor'"
                    className="pl-10 h-11 text-base bg-transparent border-0 shadow-none focus-visible:ring-0"
                  />
                </div>
                <Button data-testid="marketplace-search-button" onClick={() => search()} className="h-11 px-6 font-semibold gap-2 active:scale-[0.98] transition-transform">
                  Search vendors <ArrowRight className="w-4 h-4" />
                </Button>
              </div>

              <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <select
                  data-testid="marketplace-country-select"
                  value={country}
                  onChange={(e) => { setCountry(e.target.value); setRegion(""); search(q, trade, e.target.value, ""); }}
                  className="h-9 rounded-lg border border-border bg-card px-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">All countries</option>
                  {countries.map((cn) => <option key={cn} value={cn}>{cn}</option>)}
                </select>
                {regions.map((rg) => (
                  <button
                    key={rg}
                    data-testid={`region-chip-${rg}`}
                    onClick={() => {
                      const next = region === rg ? "" : rg;
                      setRegion(next); setCountry("");
                      search(q, trade, "", next);
                    }}
                    className={`h-9 px-3 rounded-lg text-sm font-semibold border transition-colors ${region === rg ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"}`}
                  >
                    {rg}
                  </button>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap gap-2 justify-center">
                <button
                  data-testid="trade-chip-all"
                  onClick={() => { setTrade(""); search(q, ""); }}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${trade === "" ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"}`}
                >
                  All trades
                </button>
                {trades.slice(0, 10).map((t) => (
                  <button
                    key={t}
                    data-testid={`trade-chip-${t}`}
                    onClick={() => { setTrade(t); search(q, t); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${trade === t ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {promoted.length > 0 && (
        <section className="border-b border-border bg-card">
          <div className="max-w-6xl mx-auto px-5 py-6">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-bold mb-4">Featured vendors</div>
            <div data-testid="promoted-strip" className="flex flex-wrap items-center gap-6">
              {promoted.map((v) => (
                <button key={v.id} onClick={() => navigate(`/vendor/${v.id}`)} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                  <VendorLogo vendor={v} size={44} />
                  <span className="text-sm font-semibold">{v.company_name}</span>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-5 py-10">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base md:text-lg font-bold text-slate-900 dark:text-slate-100">
            {loading ? "Searching…" : `${data?.count ?? 0} vendor${(data?.count ?? 0) === 1 ? "" : "s"} on Matiqs`}
            {trade && <span className="text-muted-foreground font-normal"> · {trade}</span>}
          </h2>
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (data?.vendors || []).length === 0 ? (
          <div data-testid="no-vendors" className="bg-card border border-border rounded-2xl p-10 text-center">
            <p className="font-semibold text-slate-900 dark:text-slate-100">No Matiqs vendor matches that yet.</p>
            <p className="text-sm text-muted-foreground mt-1 mb-5">
              We can look for companies available online instead.
            </p>
            <Button data-testid="search-online-button" onClick={searchOnline} disabled={onlineLoading || !q.trim()} className="gap-2 font-semibold">
              {onlineLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
              Find vendors available online
            </Button>
          </div>
        ) : (
          <div data-testid="vendor-list" className="space-y-3">
            {data.vendors.map((v, i) => (
              <motion.div
                key={v.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.04, 0.3) }}
                data-testid={`vendor-row-${i}`}
                className="bg-card border border-border rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 hover:border-primary/40 hover:shadow-md shadow-slate-900/5 transition-all duration-200"
              >
                <VendorLogo vendor={v} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      data-testid={`vendor-name-${i}`}
                      onClick={() => navigate(`/vendor/${v.id}`)}
                      className="font-display text-lg font-bold text-slate-900 dark:text-slate-100 hover:text-primary transition-colors text-left"
                    >
                      {v.company_name}
                    </button>
                    <span className="px-2 py-0.5 rounded-md bg-secondary text-[11px] font-semibold uppercase tracking-wide">
                      {v.vendor_type}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                    {(v.city || v.country) && (
                      <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{[v.city, v.country].filter(Boolean).join(", ")}</span>
                    )}
                    {v.business_types?.length > 0 && <span>{v.business_types.slice(0, 4).join(" · ")}</span>}
                  </div>
                  <div className="mt-2"><Badges v={v} /></div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="outline" data-testid={`view-profile-${i}`} onClick={() => navigate(`/vendor/${v.id}`)} className="h-10">
                    View profile
                  </Button>
                  <Button data-testid={`send-rfq-${i}`} onClick={() => {
                    if (isContractor) return setInviteVendor(v);
                    if (!user) return setVisitorVendor(v);
                    copyEmail(v.contact_email);
                  }} className="h-10 gap-2 font-semibold bg-brand text-brand-foreground hover:bg-brand/90 active:scale-[0.98] transition-all">
                    <Mail className="w-4 h-4" /> Send RFQ
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {online && (
          <div className="mt-10">
            <div className="flex items-center gap-2 mb-3">
              <Globe className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-base font-bold">Available online</h3>
              <span className="text-[11px] uppercase tracking-wide text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/50 px-2 py-0.5 rounded-full font-bold">
                Not Matiqs vendors — verify details
              </span>
            </div>
            {online.results.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing found online for that search.</p>
            ) : (
              <div data-testid="online-results" className="space-y-2">
                {online.results.map((r, i) => (
                  <div key={i} data-testid={`online-row-${i}`} className="bg-card border border-dashed border-border rounded-2xl p-4 flex items-start justify-between gap-4">
                    <div>
                      <div className="font-semibold text-slate-900 dark:text-slate-100">{r.company_name}</div>
                      {r.summary && <div className="text-xs text-muted-foreground mt-1 max-w-2xl">{r.summary}</div>}
                    </div>
                    <a
                      href={r.website || `https://www.google.com/search?q=${encodeURIComponent(r.company_name)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs font-semibold text-primary inline-flex items-center gap-1 shrink-0"
                    >
                      Open <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {inviteVendor && <InviteToRfqDialog vendor={inviteVendor} onClose={() => setInviteVendor(null)} />}
      {visitorVendor && <VisitorRfqDialog vendor={visitorVendor} onClose={() => setVisitorVendor(null)} />}
      {!embedded && <PublicFooter />}
    </div>
  );
}
